import React from "react";
import { formatDate, formatMoney, formatNumber } from "@/lib/utils";
import { numberToVietnameseWords } from "@/lib/numberToWords";
import { getCompanyConfig } from "@/config/company";
import { ISOPrintHeader, PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";

interface OutsourceReconciliationPrintTemplateProps {
  po?: any;
  data?: any;
  config?: any;
}

export default function OutsourceReconciliationPrintTemplate({
  po,
  data,
  config,
}: OutsourceReconciliationPrintTemplateProps) {
  const d = po || data || {};
  const now = new Date();
  const docId = d?.poId || d?.code || (d?.id ? `BB-NTGC-${d.id.slice(0, 8)}` : "");

  const receiptHistory: any[] = d?.progressLogs?.receiptHistory || [];
  const targetSent = d.qtySentTotal || (d.qtyOrdered + (d.allowanceQty || 0));
  const acceptedTotal = d.qtyAccepted || d.qtyReceived || 0;
  const rejectedTotal = d.qtyRejected || 0;
  const unitPrice = Number(d.unitPrice || 0);

  // Tính tổng tiền công & tổng tiền đền bù phế phẩm
  const grossFee = acceptedTotal * unitPrice;
  const totalCompensation = receiptHistory.reduce(
    (sum, item) => sum + Number(item.compensationAmount || 0),
    0
  );
  const netSettlement = Math.max(0, grossFee - totalCompensation);

  return (
    <div
      className="p-[10mm] text-black leading-relaxed text-[12px] bg-white w-full h-full mx-auto"
      style={{ fontFamily: "'Times New Roman', Times, serif" }}
      id="print-area"
    >
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 10mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #print-area { max-width: none !important; margin: 0 !important; width: 100% !important; padding: 0 !important; }
          table { page-break-inside: auto; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; break-inside: avoid; }
          .signature-section { page-break-inside: avoid; break-inside: avoid; }
          .settlement-section { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>

      {/* 1. HEADER CÔNG TY CHUẨN ISO */}
      <ISOPrintHeader config={config} />

      {/* 2. TIÊU ĐỀ BIỂU MẪU */}
      <PrintDocumentTitle
        title="BIÊN BẢN NGHIỆM THU & QUYẾT TOÁN GIA CÔNG"
        subtitle="SUBCONTRACTING ACCEPTANCE & AP SETTLEMENT"
        docNo={docId}
        date={d.actualReceiveDate || d.updatedAt || now}
        dateLabel="Ngày nghiệm thu"
        extraMeta={
          (d.order?.orderId || d.productionOrder?.orderId) ? (
            <span>
              Lệnh SX: <strong className="font-mono text-black">{d.productionOrder?.orderId || d.order?.orderId}</strong>
            </span>
          ) : null
        }
      />

      {/* 3. THÔNG TIN ĐỐI TÁC GIA CÔNG */}
      <PrintInfoGrid
        leftColumn={[
          {
            label: "Đơn vị gia công",
            labelEn: "Subcontractor",
            value: d.vendor?.name || "—",
            valueClassName: "font-bold text-slate-900",
          },
          {
            label: "Địa chỉ xưởng",
            labelEn: "Address",
            value: d.vendor?.address || "—",
          },
          {
            label: "Sản phẩm / Bài in",
            labelEn: "Product",
            value: d.productName || d.productionOrder?.productName || "Bán thành phẩm gia công",
          },
          {
            label: "Công đoạn gia công",
            labelEn: "Process",
            value: d.process?.name || d.productSpecs || "Gia công ngoài",
          },
        ]}
        rightColumn={[
          {
            label: "Mã đối tác",
            labelEn: "Vendor Code",
            value: d.vendor?.code || "—",
            valueClassName: "font-mono font-semibold",
          },
          {
            label: "Số điện thoại",
            labelEn: "Phone",
            value: d.vendor?.phone || d.vendor?.contactPerson || "—",
          },
          {
            label: "Mã đơn gốc",
            labelEn: "Order Ref",
            value: d.order?.orderId || d.orderId || "—",
            valueClassName: "font-mono",
          },
          {
            label: "Kế hoạch gửi đi",
            labelEn: "Planned Qty",
            value: `${formatNumber(targetSent)} cái/bộ`,
            valueClassName: "font-semibold",
          },
        ]}
      />

      {/* 4. BẢNG CHI TIẾT NGHIỆM THU TỪNG ĐỢT */}
      <div className="mt-4">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-800 mb-1.5 flex items-center justify-between">
          <span>Chi Tiết Nghiệm Thu &amp; Khấu Trừ Phế Phẩm</span>
          <span className="text-[10px] font-normal normal-case text-slate-500">
            (Đơn giá gia công: <strong>{formatMoney(unitPrice)}</strong> / cái)
          </span>
        </div>

        <table className="w-full text-left text-xs border-collapse border border-slate-300">
          <thead>
            <tr className="bg-slate-100 text-slate-800 font-semibold border-b border-slate-300">
              <th className="py-1.5 px-2 border-r border-slate-300 text-center w-8">STT</th>
              <th className="py-1.5 px-2 border-r border-slate-300 w-24">Ngày Nhận</th>
              <th className="py-1.5 px-2 border-r border-slate-300 text-right w-20">SL Nhận</th>
              <th className="py-1.5 px-2 border-r border-slate-300 text-right w-20 text-emerald-800">SL Đạt (OK)</th>
              <th className="py-1.5 px-2 border-r border-slate-300 text-right w-16 text-rose-800">Lỗi (NG)</th>
              <th className="py-1.5 px-2 border-r border-slate-300 text-right w-24">Tiền Công</th>
              <th className="py-1.5 px-2 border-r border-slate-300 text-right w-24 text-rose-800">Trừ Đền Bù</th>
              <th className="py-1.5 px-2 border-r border-slate-300 text-right w-28 text-indigo-900 font-bold">Còn Thanh Toán</th>
              <th className="py-1.5 px-2">Ghi Chú / Nguyên Nhân Lỗi</th>
            </tr>
          </thead>
          <tbody>
            {receiptHistory.length > 0 ? (
              receiptHistory.map((item, idx) => {
                const itemOk = Number(item.acceptedQty !== undefined ? item.acceptedQty : item.qty || 0);
                const itemNg = Number(item.rejectedQty || 0);
                const itemFee = itemOk * unitPrice;
                const itemComp = Number(item.compensationAmount || 0);
                const itemNet = Math.max(0, itemFee - itemComp);

                return (
                  <tr key={idx} className="border-b border-slate-200">
                    <td className="py-1.5 px-2 border-r border-slate-300 text-center font-mono">{idx + 1}</td>
                    <td className="py-1.5 px-2 border-r border-slate-300 font-mono">{formatDate(item.date)}</td>
                    <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono font-medium">
                      {formatNumber(Number(item.qty || itemOk + itemNg))}
                    </td>
                    <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono font-bold text-emerald-700">
                      {formatNumber(itemOk)}
                    </td>
                    <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono text-rose-700">
                      {itemNg > 0 ? formatNumber(itemNg) : "—"}
                    </td>
                    <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono">
                      {formatMoney(itemFee)}
                    </td>
                    <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono text-rose-600">
                      {itemComp > 0 ? `-${formatMoney(itemComp)}` : "—"}
                    </td>
                    <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono font-bold text-indigo-900">
                      {formatMoney(itemNet)}
                    </td>
                    <td className="py-1.5 px-2 text-[11px] text-slate-600">
                      {item.defectReason ? `Lỗi: ${item.defectReason}` : ""}
                      {item.compensationReason ? ` (${item.compensationReason})` : ""}
                      {item.notes ? ` - ${item.notes}` : ""}
                      {!item.defectReason && !item.notes ? "KCS Đạt chuẩn" : ""}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr className="border-b border-slate-200">
                <td className="py-1.5 px-2 border-r border-slate-300 text-center font-mono">1</td>
                <td className="py-1.5 px-2 border-r border-slate-300 font-mono">
                  {formatDate(d.actualReceiveDate || d.issueDate || now)}
                </td>
                <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono font-medium">
                  {formatNumber(d.qtyReceived || acceptedTotal)}
                </td>
                <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono font-bold text-emerald-700">
                  {formatNumber(acceptedTotal)}
                </td>
                <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono text-rose-700">
                  {rejectedTotal > 0 ? formatNumber(rejectedTotal) : "—"}
                </td>
                <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono">
                  {formatMoney(grossFee)}
                </td>
                <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono text-rose-600">
                  {totalCompensation > 0 ? `-${formatMoney(totalCompensation)}` : "—"}
                </td>
                <td className="py-1.5 px-2 border-r border-slate-300 text-right font-mono font-bold text-indigo-900">
                  {formatMoney(netSettlement)}
                </td>
                <td className="py-1.5 px-2 text-[11px] text-slate-600">
                  Nghiệm thu tổng thể đơn gia công
                </td>
              </tr>
            )}

            {/* DÒNG TỔNG CỘNG TIÊU ĐIỂM */}
            <tr className="bg-slate-100 font-bold border-t-2 border-slate-400">
              <td colSpan={2} className="py-2 px-2 text-center uppercase tracking-wide border-r border-slate-300">
                Tổng Cộng
              </td>
              <td className="py-2 px-2 text-right font-mono border-r border-slate-300">
                {formatNumber(d.qtyReceived || acceptedTotal + rejectedTotal)}
              </td>
              <td className="py-2 px-2 text-right font-mono text-emerald-700 border-r border-slate-300">
                {formatNumber(acceptedTotal)}
              </td>
              <td className="py-2 px-2 text-right font-mono text-rose-700 border-r border-slate-300">
                {rejectedTotal > 0 ? formatNumber(rejectedTotal) : "0"}
              </td>
              <td className="py-2 px-2 text-right font-mono border-r border-slate-300">
                {formatMoney(grossFee)}
              </td>
              <td className="py-2 px-2 text-right font-mono text-rose-700 border-r border-slate-300">
                {totalCompensation > 0 ? `-${formatMoney(totalCompensation)}` : "0"}
              </td>
              <td className="py-2 px-2 text-right font-mono text-indigo-900 text-[13px] border-r border-slate-300">
                {formatMoney(netSettlement)}
              </td>
              <td className="py-2 px-2 text-[11px] text-slate-600">
                {rejectedTotal === 0 ? "Đạt 100% KCS" : `Tỷ lệ lỗi: ${((rejectedTotal / (acceptedTotal + rejectedTotal)) * 100).toFixed(1)}%`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* 5. PHÂN TẦNG QUYẾT TOÁN TÀI CHÍNH */}
      <div className="settlement-section mt-4 flex justify-end">
        <div className="w-80 rounded-lg border border-slate-300 bg-slate-50 p-3 space-y-1.5 text-xs">
          <div className="flex justify-between text-slate-700">
            <span>Tổng tiền công đạt:</span>
            <span className="font-mono font-semibold">{formatMoney(grossFee)}</span>
          </div>
          {totalCompensation > 0 && (
            <div className="flex justify-between text-rose-700 font-medium">
              <span>Khấu trừ đền bù phế phẩm (Debit):</span>
              <span className="font-mono">-{formatMoney(totalCompensation)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-indigo-950 pt-1.5 border-t border-slate-300">
            <span>TỔNG THANH TOÁN:</span>
            <span className="font-mono text-emerald-800">{formatMoney(netSettlement)}</span>
          </div>
          <div className="text-[11px] italic text-slate-600 pt-0.5 border-t border-slate-200">
            Bằng chữ: <strong>{numberToVietnameseWords(netSettlement)}</strong>
          </div>
        </div>
      </div>

      {/* 6. CAM KẾT & XÁC NHẬN HAI BÊN */}
      <div className="mt-3 text-[11px] italic text-slate-600">
        * Hai bên thống nhất đối soát số lượng bán thành phẩm và chi phí gia công như trên. Khoản đền bù phế phẩm (nếu có) được khấu trừ trực tiếp vào công nợ phải trả của nhà cung cấp.
      </div>

      {/* 7. KHỐI 3 CHỮ KÝ CHUẨN ISO 9001 */}
      <div className="signature-section mt-8 grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="font-bold text-xs uppercase tracking-wider text-slate-800">
            Đại Diện Bên Thuê GC
          </div>
          <div className="text-[10px] text-slate-500 italic">(KCS / Quản đốc xưởng ký)</div>
          <div className="h-16"></div>
          <div className="font-semibold text-xs text-slate-800">Xác nhận nghiệm thu</div>
        </div>
        <div>
          <div className="font-bold text-xs uppercase tracking-wider text-slate-800">
            Kế Toán Đối Soát Công Nợ
          </div>
          <div className="text-[10px] text-slate-500 italic">(Kế toán AP lập bảng)</div>
          <div className="h-16"></div>
          <div className="font-semibold text-xs text-slate-800">Đối soát sổ cái</div>
        </div>
        <div>
          <div className="font-bold text-xs uppercase tracking-wider text-slate-800">
            Đại Diện Đơn Vị Gia Công
          </div>
          <div className="text-[10px] text-slate-500 italic">(Ký, ghi rõ họ tên &amp; đóng dấu)</div>
          <div className="h-16"></div>
          <div className="font-semibold text-xs text-slate-800">
            {d.vendor?.shortName || d.vendor?.name || "Đơn vị gia công"}
          </div>
        </div>
      </div>
    </div>
  );
}
